'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Printer, Download, CheckCircle, ShieldCheck } from 'lucide-react';
import { Order } from '@/data/mockData';

interface InvoiceModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function InvoiceModal({ order, isOpen, onClose }: InvoiceModalProps) {
  if (!isOpen || !order) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-3xl bg-white rounded-3xl border-2 border-[#E09F55] shadow-2xl p-6 sm:p-10 text-[#171711] my-8"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-[#171711] transition-colors cursor-pointer print:hidden"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Action Bar (Print / PDF) */}
        <div className="flex items-center justify-between pb-6 border-b border-gray-200 print:hidden">
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-[#387613]"></span>
            <span className="text-xs font-bold text-[#387613] uppercase tracking-wider">
              Official GST Tax Invoice Generated
            </span>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 rounded-xl bg-[#F7F4D6] hover:bg-[#DDDAC4] border border-[#E09F55] text-[#950E09] text-xs font-bold flex items-center space-x-1.5 transition-all cursor-pointer"
            >
              <Printer className="w-4 h-4 text-[#950E09]" />
              <span>Print Invoice</span>
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] text-xs font-bold flex items-center space-x-1.5 shadow-md transition-all cursor-pointer"
            >
              <Download className="w-4 h-4 text-[#F2BC6F]" />
              <span>Download PDF</span>
            </button>
          </div>
        </div>

        {/* Printable Tax Invoice Paper */}
        <div className="pt-6 space-y-6 text-xs text-[#171711]">
          
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-display text-2xl font-black text-[#950E09] tracking-wider">
                  ELAVARASI FOODS
                </span>
                <span className="font-display text-xl font-light text-[#E09F55]">
                  PVT LTD
                </span>
              </div>
              <p className="text-[11px] text-gray-500 font-medium">
                Traditional South Indian Sweets, Murukku & Savouries
              </p>
              <p className="text-[11px] text-gray-600 mt-1">
                Central Kitchen & Dispatch Center, SIDCO Industrial Complex,<br />
                Madurai & Chennai Highway, Tamil Nadu - 600032
              </p>
              <p className="text-[11px] font-semibold text-gray-700 mt-0.5">
                GSTIN: <span className="font-mono text-[#950E09]">33AABCE1234F1Z5</span> | FSSAI: <span className="font-mono text-[#387613]">12423999000182</span>
              </p>
            </div>

            <div className="text-right">
              <div className="inline-block px-3 py-1 rounded bg-[#950E09]/10 text-[#950E09] font-bold text-xs uppercase tracking-wider mb-1">
                Tax Invoice
              </div>
              <div className="text-xs">
                <span className="text-gray-500">Invoice No: </span>
                <strong className="font-mono">{order.invoiceNumber || `INV-2026-EF-${order.orderNumber}`}</strong>
              </div>
              <div className="text-xs">
                <span className="text-gray-500">Invoice Date: </span>
                <strong className="font-mono">{new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
              </div>
              <div className="text-xs">
                <span className="text-gray-500">Order Ref: </span>
                <strong className="font-mono text-[#950E09]">{order.orderNumber}</strong>
              </div>
              <div className="text-xs">
                <span className="text-gray-500">Place of Supply: </span>
                <strong>{order.shippingAddress.state} (State Code: 33)</strong>
              </div>
            </div>
          </div>

          {/* Bill To & Ship To */}
          <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-gray-50 border border-gray-200 text-xs">
            <div>
              <span className="font-bold text-gray-500 uppercase tracking-wider text-[10px] block mb-1">
                Billed To (Customer):
              </span>
              <div className="font-bold text-sm text-[#171711]">{order.customerName}</div>
              <div className="text-gray-600">{order.customerEmail}</div>
              <div className="text-gray-600">{order.customerPhone}</div>
              <div className="text-[11px] text-[#387613] font-semibold mt-1">
                Channel: {order.channel.replace(/_/g, ' ')}
              </div>
            </div>

            <div>
              <span className="font-bold text-gray-500 uppercase tracking-wider text-[10px] block mb-1">
                Shipped To (Delivery Address):
              </span>
              <div className="text-gray-800 font-medium">{order.shippingAddress.address}</div>
              <div className="text-gray-800 font-medium">
                {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Payment: <strong className="text-gray-700">{order.paymentMethod}</strong> (Status: <strong className="text-[#387613]">{order.paymentStatus}</strong>)
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-[#F7F4D6] text-[#171711] font-bold border-b border-gray-200">
                <tr>
                  <th className="p-2.5">#</th>
                  <th className="p-2.5">Item Description</th>
                  <th className="p-2.5">HSN Code</th>
                  <th className="p-2.5">Qty / Weight</th>
                  <th className="p-2.5 text-right">Unit Price</th>
                  <th className="p-2.5 text-right">Taxable Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {order.items.map((it, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="p-2.5 text-gray-500">{idx + 1}</td>
                    <td className="p-2.5 font-medium">
                      {it.name}
                      <span className="block text-[10px] text-gray-500">Pack: {it.weight}</span>
                    </td>
                    <td className="p-2.5 font-mono text-gray-600">{it.hsnCode || '21069099'}</td>
                    <td className="p-2.5 font-semibold">{it.quantity}</td>
                    <td className="p-2.5 text-right font-mono">₹{it.price}</td>
                    <td className="p-2.5 text-right font-mono font-bold">₹{it.price * it.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tax Breakdown & Grand Total */}
          <div className="flex justify-end pt-2">
            <div className="w-72 space-y-2 text-xs">
              <div className="flex justify-between text-gray-600">
                <span>Taxable Subtotal:</span>
                <span className="font-mono font-semibold">₹{order.subtotal}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping & Delivery:</span>
                <span className="font-mono font-semibold">
                  {order.deliveryFee === 0 ? 'FREE' : `₹${order.deliveryFee}`}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>CGST (2.5%):</span>
                <span className="font-mono">₹{(order.taxAmount / 2).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>SGST (2.5%):</span>
                <span className="font-mono">₹{(order.taxAmount / 2).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-[#950E09] pt-2 border-t-2 border-gray-300">
                <span>Invoice Total (INR):</span>
                <span className="font-display text-lg font-bold">₹{order.totalAmount}</span>
              </div>
            </div>
          </div>

          {/* Footer & Authorized Signatory */}
          <div className="pt-6 border-t border-gray-200 flex justify-between items-end text-[11px] text-gray-500 font-sans">
            <div className="space-y-1">
              <div className="font-semibold text-gray-700">Terms & Conditions:</div>
              <div>1. All food products are packaged under modified atmosphere and quality guaranteed.</div>
              <div>2. Goods once sold cannot be returned due to perishable nature under FSSAI regulations.</div>
              <div>3. Subject to Tamil Nadu jurisdiction.</div>
            </div>

            <div className="text-right">
              <div className="font-display font-bold text-xs text-[#950E09]">
                For ELAVARASI FOODS PVT LTD
              </div>
              <div className="h-10 flex items-center justify-end">
                <span className="font-sans italic text-gray-400 font-bold text-xs">Authorized Signatory</span>
              </div>
              <div className="text-[10px] text-gray-400">Digitally Generated Commercial Invoice</div>
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}
